import MenuList from '@components/common/menu/MenuList';
import { buildContextMenuFromType } from '@components/map/contextMenu/builderFactory';
import {
  AutomatonSectionContextCore,
  AutomatonSectionContextMenuContext,
} from '@components/map/contextMenu/builders/automatonSection';
import {
  HasEventContext,
  HasMeasureContext,
  HasPointCUASContext,
  HasQrCodeContext,
} from '@components/map/contextMenu/builders/common';
import { FeatureTypeToContextComponentEnum } from '@components/map/contextMenu/type';
import { useMapContext } from '@components/map/MapContext';
import { createErrorToastFromTradId, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { useAutomatonRight } from '@hooks/useAutomatonRight';
import { useCameraCommand } from '@hooks/useCameraCommand';
import { MeasureOriginEnum, useMeasureFromPointMenuItem } from '@hooks/useMeasureFromPointMenuItem';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole, hasSensorRoleFromActiveProfile, selectActiveUser } from '@redux/authent/authent.selectors';
import { selectActiveGlobalScreenConfiguration } from '@redux/config/config.selectors';
import { updateSelectedCameraKey, updateSelectedDoubtCheckSegmentName } from '@redux/global/global.reducer';
import { selectSelectedDoubtCheckSegmentName } from '@redux/global/global.selector';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { selectContext } from '@redux/settings/settings.selectors';
import {
  selectAutomatonSectionByName,
  selectClosestActiveMovableCamera,
  selectLowerStatesByName,
  selectMovableCameraActiveMonitoringSensors,
  selectSectionStatusByName,
  selectUpperStatesByName,
} from '@redux/situation/monitoring.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import {
  usePostAutomatonCommandMutation,
  useUpdateSensorActivationMutation,
  useUpdateSensorMaintenanceMutation,
} from '@services/c2/c2.api';
import { usePatchContextMutation } from '@services/config/setting.api';
import {
  getAutomatonSectionKeys,
  getSectionName,
  getSectionNameAndSegments,
  getSectionStartAndEndPosition,
  isSectionActive,
  isSectionInMaintenance,
  isSegmentActive,
  sendRepulsionCommand,
} from '@utils/map/automatonSection.utils';
import { findPopupByType, isOpenedPopupByType } from '@utils/popup.utils';
import { createSensorToast, getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { toCoordinates } from '@utils/validation/coordinates.utils';
import { LngLat } from 'mapbox-gl';
import { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { RoleEnum } from '@/types/authent/roles.types';
import {
  AutomatonCommandEnum,
  AutomatonSectionDefenceCommand,
  AutomatonSegmentDefenceCommand,
} from '@/types/c2/c2.types';
import { ActionEnum, Coordinates } from '@/types/commons/commons.types';
import { ContextEnum } from '@/types/config/screenConfiguration.types';
import { NotificationTypeEnum } from '@/types/data/data.types';
import { MapIdEnum } from '@/types/map.types';
import { MenuItems } from '@/types/menu.types';
import {
  AutomatonSectionInfoData,
  EventPopupData,
  HistoryPopupData,
  InfoDataTypeEnum,
  PopupDataType,
  PopupTypeEnum,
  SensorInfoData,
} from '@/types/popup.types';
import { CameraConfiguration } from '@/types/sensor/configuration.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';
import {
  AutomatonNodeStateEnum,
  AutomatonSection,
  AutomatonSectionData,
  AutomatonSectionUniqueKeys,
  AutomatonSegment,
  AutomatonSegmentTypeEnum,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';

type CommonProps = {
  clickPosition: { x: number; y: number };
  position: LngLat | undefined;
  onClose: () => void;
};
type ContextMenuContentProps = CommonProps & {
  automatonSectionData: AutomatonSectionData;
};
type AutomatonSectionContextMenuProps = CommonProps & {
  automatonSectionKeys: AutomatonSectionUniqueKeys;
};

function AutomatonSectionContextMenuContent({
  automatonSectionData,
  clickPosition,
  position,
  onClose,
}: Readonly<ContextMenuContentProps>) {
  const { mapId } = useMapContext();
  const situationTime = useAppSelector(selectSituationTime);

  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const movableLadCameras = useAppSelector(selectMovableCameraActiveMonitoringSensors);
  const closestMovableLADCamera = useSelectorWithReplayMode(selectClosestActiveMovableCamera, toCoordinates(position));
  const selectedDoubtCheckSegmentName = useAppSelector(selectSelectedDoubtCheckSegmentName);

  const hasAccessEventCommand = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_REPLAY));
  const hasAccessCamCommand = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.CAMERA }),
  );
  const { hasRolePeriSensorCommand, hasRoleSensorMaintenance, hasRoleRepulsionCommand, hasRoleDetectionCommand } =
    useAutomatonRight();

  const user = useAppSelector(selectActiveUser);
  const currentContext = useAppSelector(selectContext);
  const activeGlobalScreenConfiguration = useAppSelector(selectActiveGlobalScreenConfiguration);
  const { automaton, cabinet, section } = automatonSectionData;
  const doubtCheckContextIsPresent = !!activeGlobalScreenConfiguration?.screenConfigurations.some(
    (screenConfig) => screenConfig.context === ContextEnum.DOUBT_PERIM && screenConfig.gridLayout,
  );

  const { name, segments } = getSectionNameAndSegments(section);

  const upperSegment = section.upperSegment ?? null;
  const lowerSegment = section.lowerSegment ?? null;

  const lowerStates = useSelectorWithReplayMode(selectLowerStatesByName, name) ?? {};
  const upperStates = useSelectorWithReplayMode(selectUpperStatesByName, name) ?? {};
  const sectionStatus = useSelectorWithReplayMode(selectSectionStatusByName, lowerSegment?.name ?? upperSegment?.name);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const updateGlobalSelectedDoubtCheckSegmentName = useWithDispatch(updateSelectedDoubtCheckSegmentName);
  const updateGlobalSelectedCameraCode = useWithDispatch(updateSelectedCameraKey);
  const [postRepulsionCommand, { isLoading: isRepulsionCommandLoading }] = usePostAutomatonCommandMutation();
  const [patchMaintenance, { isLoading: isMaintenanceLoading }] = useUpdateSensorMaintenanceMutation();
  const [patchActivation, { isLoading: isActivationLoading }] = useUpdateSensorActivationMutation();

  const [patchContext] = usePatchContextMutation();

  const isLowerRepulsionOff = !!lowerStates[AutomatonNodeStateEnum.BT_DETECTION];
  const isUpperRepulsionOff = !!upperStates[AutomatonNodeStateEnum.BT_DETECTION];
  const isRepulsionOff = (!lowerSegment || isLowerRepulsionOff) && (!upperSegment || isUpperRepulsionOff);
  const canSwitchRepulsion = isRepulsionOff ? hasRoleRepulsionCommand : hasRoleDetectionCommand;

  const useCommand = useCameraCommand({
    monitoringSensor: closestMovableLADCamera,
  });

  const sectionInfo = findPopupByType(popups, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_AUTOMATON_SECTION);
  const sensorInfo = findPopupByType(popups, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_SENSOR);
  const segmentHistoryPopup = findPopupByType(popups, PopupTypeEnum.HISTORY, NotificationTypeEnum.SEGMENT);

  const handleOpenSectionInfo = useCallback(() => {
    updatePopup({
      mapId: mapId,
      popup: {
        type: PopupTypeEnum.INFO,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          type: InfoDataTypeEnum.INFO_AUTOMATON_SECTION,
          featureValue: getAutomatonSectionKeys(automatonSectionData),
        },
      },
    });
    onClose();
  }, [updatePopup, mapId, automatonSectionData, clickPosition.x, clickPosition.y, onClose]);

  function handleOpenSectionCabinetInfo() {
    updatePopup({
      mapId: mapId,
      popup: {
        type: PopupTypeEnum.INFO,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          type: InfoDataTypeEnum.INFO_SENSOR,
          featureValue: { sensorUniqueKey: getSensorUniqueKey(cabinet) },
        } as SensorInfoData,
      },
    });
    onClose();
  }

  // On prend arbitrairement le segment du haut. En théorie les 2 segments ont les mêmes caméras liées, mais en
  // pratique Jonathan les a mis que sur le segment haut dans son fichier d'import :/
  const segmentWithCamera = (upperSegment?.name ?? lowerSegment?.name) as string;

  function openAttachedPerimeterCameras() {
    updateGlobalSelectedDoubtCheckSegmentName(segmentWithCamera);
    onClose();
  }

  const segmentAltitude = ((upperSegment ?? lowerSegment) as AutomatonSegment).altitude ?? null;

  function handleLookAt(configuration: CameraConfiguration) {
    if (position) {
      const sectionPosition = getSectionStartAndEndPosition(section);
      useCommand.lookAt(
        {
          latitude: position.lat,
          longitude: position.lng,
          type: '2D',
        },
        {
          longitude: sectionPosition.sectionStart[0],
          latitude: sectionPosition.sectionStart[1],
          type: '2D',
        },
        {
          longitude: sectionPosition.sectionEnd[0],
          latitude: sectionPosition.sectionEnd[1],
          type: '2D',
        },
        segmentAltitude,
        null,
        true,
        configuration,
      );
      updateGlobalSelectedCameraCode(configuration.uniqueKey);
      onClose();
    }
  }

  function handleSectionActivation(segments: AutomatonSegment[]) {
    const isActive = automaton.active && cabinet.active && segments.every((segment) => segment.active);
    const section = {
      upperSegment: segments.find((segment) => segment.segmentType === AutomatonSegmentTypeEnum.UPPER),
      lowerSegment: segments.find((segment) => segment.segmentType === AutomatonSegmentTypeEnum.LOWER),
    } as AutomatonSection;
    const toastParams = {
      commandValues: { state: !isActive },
      command: 'sensors.commands.activationCommand',
      name: getSectionName(section),
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    patchActivation({
      id: automaton.id,
      site: automaton.site,
      appCode: automaton.appCode,
      active: !isActive,
      type: SubSensorTypeEnum.AUTOMATON_SEGMENT,
      codes: segments.map((segment) => segment.code),
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
    onClose();
  }

  function handleSegmentRepulsion(enableRepulsion: boolean, segment: AutomatonSegment) {
    const command = {
      type: AutomatonCommandEnum.AUTOMATON_SEGMENT_COMMAND_DEFENCE,
      sensorCode: automaton.code,
      cabinetId: cabinet.nodeId,
      segmentId: segment.nodeId,
      defence: enableRepulsion,
      segmentType: segment.segmentType,
    } as AutomatonSegmentDefenceCommand;

    sendRepulsionCommand(
      command,
      postRepulsionCommand,
      enableRepulsion,
      name,
      true,
      automaton.site,
      automaton.appCode,
      segment.segmentType,
    );
    onClose();
  }

  function handleSectionRepulsion(enableRepulsion: boolean) {
    const command = {
      type: AutomatonCommandEnum.AUTOMATON_SECTION_COMMAND_DEFENCE,
      sensorCode: automaton.code,
      cabinetId: cabinet.nodeId,
      segmentId: segments[0].nodeId,
      defence: enableRepulsion,
    } as AutomatonSectionDefenceCommand;

    sendRepulsionCommand(
      command,
      postRepulsionCommand,
      enableRepulsion,
      name,
      false,
      automaton.site,
      automaton.appCode,
    );
    onClose();
  }

  function handleSectionMaintenance(segments: AutomatonSegment[]) {
    const isMaintenance = segments.some((segment) => segment.maintenance);
    const section = {
      upperSegment: segments.find((segment) => segment.segmentType === AutomatonSegmentTypeEnum.UPPER),
      lowerSegment: segments.find((segment) => segment.segmentType === AutomatonSegmentTypeEnum.LOWER),
    } as AutomatonSection;
    const toastParams = {
      commandValues: { state: !isMaintenance },
      command: 'sensors.commands.maintenanceCommand',
      name: getSectionName(section),
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    patchMaintenance({
      id: automaton.id,
      site: automaton.site,
      appCode: automaton.appCode,
      maintenance: !isMaintenance,
      type: SubSensorTypeEnum.AUTOMATON_SEGMENT,
      codes: segments.map((segment) => segment.code),
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
    onClose();
  }

  function setupForPerimeterDoubtCheckContext() {
    patchContext({ login: user.login, context: ContextEnum.DOUBT_PERIM });
    closestMovableLADCamera && handleLookAt(closestMovableLADCamera.configuration);
    openAttachedPerimeterCameras();
  }

  const sectionCenterPosition: Coordinates | null = upperSegment?.position ?? lowerSegment?.position ?? null;

  function handlePopup() {
    const upperIdentifier = upperSegment
      ? `${upperSegment.uniqueKey}-${upperSegment.nodeId}_${upperSegment.segmentType}`
      : undefined;
    const lowerIdentifier = lowerSegment
      ? `${lowerSegment.uniqueKey}-${lowerSegment.nodeId}_${lowerSegment.segmentType}`
      : undefined;

    const identifiers: string[] = [upperIdentifier, lowerIdentifier].filter((value) => value !== undefined);
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.HISTORY,
        data: {
          identifiers,
          type: NotificationTypeEnum.SEGMENT,
          sensorName: name,
          sensorType: SubSensorTypeEnum.AUTOMATON_SEGMENT,
          openingDate: situationTime ? new Date(situationTime) : new Date(),
        },
        position: { x: clickPosition.x, y: clickPosition.y },
      },
    });
    onClose();
  }

  const handleCreateEventClick = useCallback(() => {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.EVENT,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          coordinates: position,
          date: new Date().toISOString(),
        } as unknown as EventPopupData,
      },
    });
    onClose();
  }, [clickPosition.x, clickPosition.y, mapId, onClose, position, updatePopup]);

  const openPopup = (popupDataType: PopupDataType) => {
    updatePopup({
      mapId,
      popup: {
        ...popupDataType,
        position: { x: clickPosition.x, y: clickPosition.y },
      },
    });
  };

  const openGpsQrCodePopup = () => {
    if (upperSegment?.segmentPosition) {
      openPopup({
        type: PopupTypeEnum.GPS_QR_CODE,
        data: {
          coordinates: upperSegment.segmentPosition[0],
        },
      });
      onClose();
    }
  };

  //todo move this inside automatonSection.ts
  const modifySectionSubMenuItem: MenuItems = {
    upPart: {
      label: 'contextmenu.actions.modifySection.upPart',
      isDisabled: !upperSegment,
      subMenuItems: {
        repulsionOn: {
          label: 'contextmenu.actions.modifySection.repulsionOn',
          isHidden: !hasRolePeriSensorCommand,
          isLoading: isRepulsionCommandLoading,
          isDisabled:
            !isSegmentActive(automaton, cabinet, upperSegment) ||
            sectionStatus?.specificState === SpecificStateEnum.SLEEP ||
            !isUpperRepulsionOff,
          onAction: () => upperSegment && handleSegmentRepulsion(true, upperSegment),
        },
        repulsionOff: {
          label: 'contextmenu.actions.modifySection.repulsionOff',
          isHidden: !hasRolePeriSensorCommand,
          isLoading: isRepulsionCommandLoading,
          isDisabled:
            !isSegmentActive(automaton, cabinet, upperSegment) ||
            sectionStatus?.specificState === SpecificStateEnum.SLEEP ||
            isUpperRepulsionOff,
          onAction: () => upperSegment && handleSegmentRepulsion(false, upperSegment),
        },
        switchMaintenanceMode: {
          label: `contextmenu.actions.${upperSegment?.maintenance ? 'maintenanceOff' : 'maintenanceOn'}`,
          isHidden: !hasRoleSensorMaintenance,
          isLoading: isMaintenanceLoading,
          isDisabled:
            !isSegmentActive(automaton, cabinet, upperSegment) || automaton.maintenance || cabinet.maintenance,
          onAction: () => upperSegment && handleSectionMaintenance([upperSegment]),
        },
        switchSegmentConnection: {
          label: `contextmenu.actions.modifySection.${upperSegment?.active ? 'disconnectSegment' : 'reconnectSegment'}`,
          isHidden: !hasRoleSensorMaintenance,
          isLoading: isActivationLoading,
          onAction: () => upperSegment && handleSectionActivation([upperSegment]),
        },
      },
    },
    bottomPart: {
      label: 'contextmenu.actions.modifySection.bottomPart',
      isDisabled: !lowerSegment,
      subMenuItems: {
        repulsionOn: {
          label: 'contextmenu.actions.modifySection.repulsionOn',
          isHidden: !hasRolePeriSensorCommand,
          isLoading: isRepulsionCommandLoading,
          isDisabled:
            !isSegmentActive(automaton, cabinet, lowerSegment) ||
            sectionStatus?.specificState === SpecificStateEnum.SLEEP ||
            !isLowerRepulsionOff,
          onAction: () => lowerSegment && handleSegmentRepulsion(true, lowerSegment),
        },
        repulsionOff: {
          label: 'contextmenu.actions.modifySection.repulsionOff',
          isHidden: !hasRolePeriSensorCommand,
          isLoading: isRepulsionCommandLoading,
          isDisabled:
            !isSegmentActive(automaton, cabinet, lowerSegment) ||
            sectionStatus?.specificState === SpecificStateEnum.SLEEP ||
            isLowerRepulsionOff,
          onAction: () => lowerSegment && handleSegmentRepulsion(false, lowerSegment),
        },
        switchMaintenanceMode: {
          label: `contextmenu.actions.${lowerSegment?.maintenance ? 'maintenanceOff' : 'maintenanceOn'}`,
          isHidden: !hasRoleSensorMaintenance,
          isLoading: isMaintenanceLoading,
          isDisabled:
            !isSegmentActive(automaton, cabinet, lowerSegment) || automaton.maintenance || cabinet.maintenance,
          onAction: () => lowerSegment && handleSectionMaintenance([lowerSegment]),
        },
        switchSegmentConnection: {
          label: `contextmenu.actions.modifySection.${lowerSegment?.active ? 'disconnectSegment' : 'reconnectSegment'}`,
          isHidden: !hasRoleSensorMaintenance,
          isLoading: isActivationLoading,
          onAction: () => lowerSegment && handleSectionActivation([lowerSegment]),
        },
      },
    },
  };

  const [label, enableMeasure] = useMeasureFromPointMenuItem({
    closeContextMenu: onClose,
    position: sectionCenterPosition,
    measureOrigin: MeasureOriginEnum.PERIMETER_SECTION,
  });

  const qrCodeContext: HasQrCodeContext = {
    position: position ?? null,
    openGpsQrCodePopup,
  };
  const measureContext: HasMeasureContext = {
    position: position ?? null,
    label,
    enableMeasure,
  };
  const eventContext: HasEventContext = {
    popups,
    accessEventCommand: hasAccessEventCommand,
    isOpenedPopupByType,
    handleCreateEventClick,
  };
  const pointCUASContext: HasPointCUASContext = {
    position: position ?? null,
    accessCamCommand: hasAccessCamCommand,
    movableLadCameras: movableLadCameras.toSorted((cam, cam2) => {
      if (
        getSensorUniqueKey(cam) === upperSegment?.ladCameraUniqueKey ||
        (getSensorUniqueKey(cam) === lowerSegment?.ladCameraUniqueKey &&
          getSensorUniqueKey(cam2) !== upperSegment?.ladCameraUniqueKey)
      ) {
        return -1;
      }
      return 1;
    }),
    handleLookAt,
  };
  const automatonSectionContext: AutomatonSectionContextCore = {
    isSectionInfoDisabled:
      !!sectionInfo && (sectionInfo.data as AutomatonSectionInfoData).featureValue.sectionName === name,
    handleOpenSectionInfo,
    isCabinetInfoDisabled:
      !!sensorInfo && (sensorInfo.data as SensorInfoData).featureValue.sensorUniqueKey === cabinet.uniqueKey,
    handleOpenSectionCabinetInfo,
    isPerimeterDoubtCheckHidden: currentContext !== ContextEnum.LIVE,
    isPerimeterDoubtCheckDisabled: !doubtCheckContextIsPresent,
    setupForPerimeterDoubtCheckContext,
    isRepulsionOff,
    isRepulsionCommandHidden: !hasRolePeriSensorCommand,
    isRepulsionCommandLoading,
    isRepulsionCommandDisabled:
      !isSectionActive(automaton, cabinet, section) ||
      sectionStatus?.specificState === SpecificStateEnum.SLEEP ||
      !canSwitchRepulsion,
    handleSectionRepulsion,
    isMaintenanceOn: isSectionInMaintenance(automaton, cabinet, section),
    isMaintenanceHidden: !hasRoleSensorMaintenance,
    isMaintenanceLoading,
    isMaintenanceDisabled:
      automaton.maintenance || cabinet.maintenance || !isSectionActive(automaton, cabinet, section),
    segments,
    handleSectionMaintenance,
    isSectionConnected: isSectionActive(automaton, cabinet, section),
    isSectionOptionHidden: !hasRoleSensorMaintenance,
    isSectionOptionLoading: isActivationLoading,
    handleSectionActivation,
    isDisplayPerimeterCameraOptionHidden: currentContext !== ContextEnum.DOUBT_PERIM,
    isDisplayPerimeterCameraOptionDisabled: segmentWithCamera === selectedDoubtCheckSegmentName,
    openAttachedPerimeterCameras,
    isModifySectionOptionHidden: !hasRoleSensorMaintenance && !hasRolePeriSensorCommand,
    modifySectionSubMenuItem,
    isHistoryOptionDisabled:
      !!segmentHistoryPopup &&
      (segmentHistoryPopup.data as HistoryPopupData).identifiers.some(
        (value) => value === upperSegment?.uniqueKey || value === lowerSegment?.uniqueKey,
      ),
    handlePopup,
  };
  const ctx: AutomatonSectionContextMenuContext = {
    ...automatonSectionContext,
    ...qrCodeContext,
    ...eventContext,
    ...measureContext,
    ...pointCUASContext,
  };

  const menuItems = buildContextMenuFromType<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>(
    FeatureTypeToContextComponentEnum.AUTOMATON_SECTION,
    mapId,
    ctx,
  );

  return <MenuList menuItems={menuItems} />;
}

export function AutomatonSectionContextMenu({
  automatonSectionKeys,
  clickPosition,
  position,
  onClose,
}: Readonly<AutomatonSectionContextMenuProps>) {
  const { mapId } = useMapContext();
  const value = useSelectorWithReplayMode(selectAutomatonSectionByName, automatonSectionKeys.sectionName);

  if (!value) {
    return null;
  }

  return mapId === MapIdEnum.REPLAY ? (
    <ErrorBoundary fallback={<></>} onError={() => createErrorToastFromTradId('error.sensorReplay.contextMenu')}>
      <AutomatonSectionContextMenuContent
        clickPosition={clickPosition}
        position={position}
        automatonSectionData={value}
        onClose={onClose}
      />
    </ErrorBoundary>
  ) : (
    <AutomatonSectionContextMenuContent
      clickPosition={clickPosition}
      position={position}
      automatonSectionData={value}
      onClose={onClose}
    />
  );
}
